import { Constructor } from '@angular/cdk/table';
import {
  FormGroup,
  FormBuilder,
  UntypedFormArray,
  FormControl,
  FormArray,
  ValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import { Observable, Subject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { IAutoCompleteData } from 'src/app/shared-form-sections/interfaces/i-auto-complete-data';
import { SponsorService } from '../services/sponsor.service';
import { ISponsor } from 'src/app/core/models/i-sponsor';
import { FORM_FIELDS } from 'src/app/article-content/components/article-content-form/constants/form-fields';
import { ISponsorResponse } from 'src/app/core/models/i-sponsor-response';
import { FORM_PATHS } from 'src/app/article-content/components/article-content-form/constants/form-paths';

type WithSponsorsFormSectionType = {
  sponsorList: ISponsor[];
  searchSponsorList: IAutoCompleteData[];
  searchedSponsor: FormControl;
  isSearchingSponsors: boolean;
  _initSponsors(selectedSponsor: ISponsor, isSponsored: boolean);
  _initSponsorValidation(): ValidatorFn;
  _initSelectedSponsorValueChanges(
    getPreviousSelectedValue: () => ISponsor | null,
    getPreviousSponsoredValue: () => boolean | null
  );
  _buildSelectedSponsor();
  _buildIsSponsored();
};

type WithSponsorsFormSectionRequerimentsBase = {
  form: FormGroup;
  FORM_PATHS: { SELECTED_SPONSOR: () => string; IS_SPONSORED: () => string };
  destroy$: Subject<boolean>;
  sponsorService: SponsorService;
  formBuilder: FormBuilder;
};

export function WithSponsorsFormSection<
  TBase extends Constructor<WithSponsorsFormSectionRequerimentsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithSponsorsFormSectionType;
  prototype: WithSponsorsFormSectionType;
} & TBase {
  return class extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    sponsorList: ISponsor[];
    searchSponsorList: IAutoCompleteData[];
    searchedSponsor: FormControl = this.formBuilder.control('');
    isSearchingSponsors: boolean;

    _initSponsors(selectedSponsor: ISponsor, isSponsored: boolean): void {
      if (selectedSponsor) {
        this.form
          .get(this.FORM_PATHS.SELECTED_SPONSOR())
          .setValue(selectedSponsor);
      }

      if (isSponsored !== null) {
        this.form.get(this.FORM_PATHS.IS_SPONSORED()).setValue(isSponsored);
      }

      this.searchedSponsor.valueChanges
        .pipe(
          distinctUntilChanged(),
          debounceTime(500),
          tap(() => {
            this.isSearchingSponsors = true;
          }),
          switchMap((searchedValue) => {
            const getDataObs$ = searchedValue
              ? this.sponsorService
                  .getFilteredBySearchText$(
                    typeof searchedValue === 'string' ? searchedValue : ''
                  )
                  .pipe(
                    map(
                      (sponsorsResponse: ISponsorResponse) =>
                        sponsorsResponse.results
                    )
                  )
              : of(null);

            return getDataObs$.pipe(
              finalize(() => {
                this.isSearchingSponsors = false;
              })
            );
          })
        )
        .subscribe({
          next: (x) => {
            if (x) {
              this.sponsorList = x;
              this.searchSponsorList = x.map((p) => {
                return { displayName: p.name, id: p.id };
              });
            }
          },
        });
    }

    _initSelectedSponsorValueChanges(
      getPreviousSelectedValue: () => ISponsor | null,
      getPreviousSponsoredValue: () => boolean | null
    ): void {
      this.form
        .get(this.FORM_PATHS.SELECTED_SPONSOR())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue === getPreviousSelectedValue()) {
              this.form
                .get(this.FORM_PATHS.SELECTED_SPONSOR())
                .markAsPristine();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.IS_SPONSORED())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue) {
              let contentListControl = this.form.get(
                FORM_PATHS.CONTENT_LISTS()
              ) as FormArray;

              contentListControl.clear();
            }
            if (newValue === getPreviousSponsoredValue()) {
              this.form.get(this.FORM_PATHS.IS_SPONSORED()).markAsPristine();
            }
          },
        });
    }

    _initSponsorValidation(): ValidatorFn {
      return (formGroup: FormGroup): ValidationErrors | null => {
        const selectedSponsorControl = formGroup.get(
          FORM_FIELDS.SELECTED_SPONSOR
        );
        const isSponsoredControl = formGroup.get(FORM_FIELDS.IS_SPONSORED);

        if (!isSponsoredControl.value) {
          return null;
        }

        let errors: ValidationErrors = {};

        if (isSponsoredControl.value && !selectedSponsorControl.value) {
          errors[FORM_FIELDS.SELECTED_SPONSOR] = true;
          selectedSponsorControl.setErrors(errors);
        }

        return Object.keys(errors).length === 0 ? null : errors;
      };
    }

    _buildSelectedSponsor(): ISponsor {
      return this.form.get(this.FORM_PATHS.SELECTED_SPONSOR()).value;
    }

    _buildIsSponsored(): boolean {
      return this.form.get(this.FORM_PATHS.IS_SPONSORED()).value;
    }
  };
}
